import { useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, LoaderCircle, Search, UserRound, X } from "lucide-react";
import { AppEmptyState, AppSheet } from "@/shared/components/AppUi";
import { ProfileAvatar } from "@/shared/components/ProfileAvatar";
import {
  buscarAlunosEvolucaoProfessor,
  obterCriteriosEvolucaoProfessor,
  registrarEvolucaoTecnicaPerfilProfessor,
  type ProfessorEvolutionStudent,
  type ProfessorTechnicalCriterion,
} from "./professorApi";
import { TechnicalEvaluationMobile } from "./TechnicalEvaluationMobile";

type View = "students" | "student" | "evaluate";

export function ProfessorClassStudentsSheet({
  open,
  turmaId,
  turmaNome,
  onClose,
}: {
  open: boolean;
  turmaId: string;
  turmaNome: string;
  onClose: () => void;
}) {
  const [view, setView] = useState<View>("students");
  const [query, setQuery] = useState("");
  const [students, setStudents] = useState<ProfessorEvolutionStudent[]>([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ProfessorEvolutionStudent | null>(null);
  const [criteria, setCriteria] = useState<ProfessorTechnicalCriterion[]>([]);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [observation, setObservation] = useState("");
  const [shared, setShared] = useState(true);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!open) return;
    setView("students");
    setSelected(null);
    setQuery("");
    setFeedback("");
  }, [open, turmaId]);

  useEffect(() => {
    if (!open || view !== "students") return;
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      buscarAlunosEvolucaoProfessor(query.trim(), 1, 20, turmaId)
        .then(result => {
          if (!active) return;
          setStudents(result.itens);
          setPage(1);
          setMore(result.temMais);
        })
        .finally(() => { if (active) setLoading(false); });
    }, 180);
    return () => { active = false; window.clearTimeout(timer); };
  }, [open, query, turmaId, view]);

  async function loadMore() {
    if (loading || !more) return;
    setLoading(true);
    try {
      const next = page + 1;
      const result = await buscarAlunosEvolucaoProfessor(query.trim(), next, 20, turmaId);
      setStudents(current => [...current, ...result.itens]);
      setPage(next);
      setMore(result.temMais);
    } finally { setLoading(false); }
  }

  function openStudent(student: ProfessorEvolutionStudent) {
    setSelected(student);
    setFeedback("");
    setView("student");
  }

  async function openEvaluation(student = selected) {
    if (!student) return;
    setSelected(student);
    setView("evaluate");
    setEvaluationLoading(true);
    setFeedback("");
    setScores({});
    setObservation("");
    setShared(true);
    try {
      setCriteria((await obterCriteriosEvolucaoProfessor(turmaId, student.alunoId)).slice(0, 4));
    } finally { setEvaluationLoading(false); }
  }

  async function saveEvaluation() {
    if (!selected || Object.keys(scores).length === 0 || saving) return;
    setSaving(true);
    setFeedback("");
    try {
      await registrarEvolucaoTecnicaPerfilProfessor(
        turmaId,
        selected.alunoId,
        todayIso(),
        fallbackUuid(),
        shared,
        observation,
        criteria.filter(item => scores[item.chave]).map(item => ({ chave: item.chave, valor: scores[item.chave] })),
      );
      setFeedback("Avaliação registrada.");
      setView("student");
    } catch {
      setFeedback("Não foi possível salvar a avaliação. Tente novamente.");
    } finally { setSaving(false); }
  }

  const title = view === "students" ? turmaNome : selected?.nome ?? turmaNome;
  const kicker = view === "students" ? "Turma" : view === "evaluate" ? "Avaliar agora" : "Aluno";

  return <AppSheet open={open} title={title} kicker={kicker} onClose={onClose}>
    {view !== "students" && <button type="button" onClick={() => setView(view === "evaluate" ? "student" : "students")} className="mb-4 flex min-h-10 items-center gap-2 text-xs font-bold text-teal-light"><ArrowLeft className="h-4 w-4"/>{view === "evaluate" ? "Voltar ao aluno" : "Voltar à turma"}</button>}

    {view === "students" && <>
      <label className="flex min-h-14 items-center gap-3 rounded-2xl border border-white/10 bg-white/[.03] px-4"><Search className="h-4 w-4 text-teal-light"/><input value={query} onChange={event => setQuery(event.target.value)} className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-white/30" placeholder="Buscar aluno"/>{query && <button type="button" onClick={() => setQuery("")} aria-label="Limpar busca"><X className="h-4 w-4 text-white/40"/></button>}</label>
      <div className="mt-4 space-y-2">
        {students.map(student => <div key={student.alunoId} className="flex items-center gap-2 rounded-2xl border border-white/8 bg-white/[.025] p-2">
          <button type="button" onClick={() => openStudent(student)} className="flex min-w-0 flex-1 items-center gap-3 rounded-xl p-1 text-left">
            <ProfileAvatar src={student.fotoUrl} name={student.nome} size="md" shape="rounded"/>
            <span className="min-w-0 flex-1"><strong className="block truncate text-sm text-white/82">{student.nome}</strong><small className="mt-1 block truncate text-[10px] text-white/35">{student.faixaAtual ? `Faixa ${student.faixaAtual}${student.graus != null ? ` · ${student.graus} graus` : ""}` : turmaNome}</small></span>
            <ChevronRight className="h-4 w-4 shrink-0 text-white/20"/>
          </button>
          <button type="button" onClick={() => void openEvaluation(student)} className="min-h-10 shrink-0 rounded-xl border border-teal/20 bg-teal/[.06] px-3 text-[10px] font-black text-teal-light">Avaliar</button>
        </div>)}
        {loading && students.length === 0 && <div className="grid min-h-32 place-items-center"><LoaderCircle className="h-6 w-6 animate-spin text-teal"/></div>}
        {!loading && students.length === 0 && <AppEmptyState title="Nenhum aluno encontrado" detail="A turma não possui alunos ativos que você possa acompanhar."/>}
        {more && <button type="button" disabled={loading} onClick={() => void loadMore()} className="min-h-12 w-full rounded-2xl border border-white/10 bg-white/[.03] text-xs font-bold text-teal-light disabled:opacity-50">{loading ? "Carregando…" : "Carregar mais"}</button>}
      </div>
    </>}

    {view === "student" && selected && <div>
      <div className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[.025] p-4"><ProfileAvatar src={selected.fotoUrl} name={selected.nome} size="lg" shape="rounded"/><div className="min-w-0"><strong className="block truncate text-base text-white/85">{selected.nome}</strong><span className="mt-1 block text-[11px] text-white/40">{selected.faixaAtual ? `Faixa ${selected.faixaAtual}${selected.graus != null ? ` · ${selected.graus} graus` : ""}` : turmaNome}</span></div></div>
      {feedback && <p className={`mt-3 rounded-xl border p-3 text-xs ${feedback.startsWith("Avaliação") ? "border-teal/20 bg-teal/[.05] text-teal-light" : "border-red-300/20 bg-red-300/[.05] text-red-100"}`}>{feedback}</p>}
      <button type="button" onClick={() => void openEvaluation()} className="premium-primary-button mt-4 flex min-h-14 w-full items-center justify-center gap-2"><UserRound className="h-4 w-4"/>Avaliar agora</button>
      <p className="mt-3 text-center text-[10px] leading-4 text-white/30">A mesma avaliação técnica usada na Chamada e em Evolução.</p>
    </div>}

    {view === "evaluate" && selected && <>
      {evaluationLoading ? <div className="grid min-h-40 place-items-center"><LoaderCircle className="h-6 w-6 animate-spin text-teal"/></div> : <TechnicalEvaluationMobile criteria={criteria} scores={scores} onScore={(key, value) => setScores(current => ({ ...current, [key]: value }))} observation={observation} onObservation={setObservation} shared={shared} onShared={setShared}/>} 
      {!evaluationLoading && <button type="button" disabled={saving || Object.keys(scores).length === 0} onClick={() => void saveEvaluation()} className="premium-primary-button mt-4 min-h-14 w-full disabled:opacity-40">{saving ? "Salvando…" : shared ? "Salvar avaliação" : "Salvar para equipe"}</button>}
    </>}
  </AppSheet>;
}

function todayIso() { const date = new Date(); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }
function fallbackUuid() { return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, char => { const random = Math.random() * 16 | 0; const value = char === "x" ? random : (random & 0x3 | 0x8); return value.toString(16); }); }
